import { useCallback, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { downloadFile, useLazyDownloadGlobalFileRequestQuery } from '../../../store/media/file'
import { useLogger } from '../../../components/Logger/hooks/useLogger'
import { AudioPlayer } from '../common/audioPlayer'
import { type Nullable } from '../../../common/Nullable'

const DEFAULT_COMPLIANCE_AUDIO_URL = 'https://static.zocks.io/assets/compliance/default_compliance_audio.wav'
const FALLBACK_COMPLIANCE_AUDIO_URL = `${import.meta.env.BASE_URL}assets/default_compliance_audio.wav`

export const useComplianceAudio = () => {
  const logger = useLogger()
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId })
  const complianceNotice = account?.data?.effectiveConfig?.complianceConfig?.complianceNotice
  const audioEnabled = !!complianceNotice?.inPersonAudioEnabled
  const audioFileId = complianceNotice?.inPersonAudioFileConverted?.fileId ?? complianceNotice?.inPersonAudioFileOriginal?.fileId ?? null

  const [downloadFileRequest] = useLazyDownloadGlobalFileRequestQuery()
  const playerRef = useRef<Nullable<AudioPlayer>>(null)
  const initPromiseRef = useRef<Promise<void>>(Promise.resolve())
  const playedSessionIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!audioEnabled) {
      return
    }
    const player = new AudioPlayer()
    playerRef.current = player

    const init = async () => {
      try {
        try {
          if (audioFileId) {
            const {
              data: { location },
            } = await downloadFileRequest({ accountId, fileId: audioFileId }).unwrap()
            const response = await downloadFile(location)
            await player.init(response.data)
          } else {
            await player.init(DEFAULT_COMPLIANCE_AUDIO_URL)
          }
        } catch (error: unknown) {
          Sentry.captureException(error)
          await logger.warn('[useComplianceAudio] Failed to download compliance notice audio, falling back to bundled audio', error)
          await player.init(FALLBACK_COMPLIANCE_AUDIO_URL)
        }
        await logger.info('[useComplianceAudio] Compliance notice audio initialized', { audioFileId })
      } catch (error: unknown) {
        Sentry.captureException(error)
        await logger.error('[useComplianceAudio] Failed to initialize compliance notice audio', error)
      }
    }
    initPromiseRef.current = init()

    return () => {
      playerRef.current = null
      player.close().catch(async (error: unknown) => {
        Sentry.captureException(error)
        await logger.warn('[useComplianceAudio] Error closing compliance audio player', error)
      })
    }
  }, [accountId, audioEnabled, audioFileId, downloadFileRequest, logger])

  const playComplianceAudio = useCallback(
    async (sessionId?: string) => {
      if (!audioEnabled) {
        return
      }
      await initPromiseRef.current
      const player = playerRef.current
      if (!player?.initialized) {
        await logger.warn('[useComplianceAudio] Failed to play compliance notice audio: player is not initialized', { sessionId })
        return
      }
      if (player.playing) {
        await logger.warn('[useComplianceAudio] Compliance notice audio is already playing', { sessionId })
        return
      }
      if (sessionId && playedSessionIdsRef.current.has(sessionId)) {
        await logger.info('[useComplianceAudio] Compliance notice already played once, skipping', { sessionId })
        return
      }
      try {
        await logger.info('[useComplianceAudio] Start to play compliance notice audio', { sessionId })
        await player.play({
          playingFinishedCallback: () => {
            if (sessionId) {
              playedSessionIdsRef.current.add(sessionId)
            }
            logger.info('[useComplianceAudio] Compliance notice audio played', { sessionId }).then(() => {})
          },
        })
      } catch (error: unknown) {
        Sentry.captureException(error)
        await logger.error('[useComplianceAudio] Error during playing compliance notice audio', error)
      }
    },
    [audioEnabled, logger],
  )

  return { complianceAudioEnabled: audioEnabled, playComplianceAudio }
}
